// Task Service - Single Responsibility: Manage tasks only
export interface RecurrenceRule {
  frequency: 'daily' | 'weekdays' | 'weekly' | 'biweekly' | 'monthly' | 'custom'
  interval?: number
  days_of_week?: number[]
  day_of_month?: number
  time?: string
  end_date?: string
  occurrences?: number
}

/** One gap the task score found, with the question every surface asks for it. */
export interface TaskScoreGap {
  component: string
  tier: 'A' | 'B' | 'C'
  weight: number
  question: string
  kind: 'text' | 'duration' | 'when' | 'belongs' | 'stakes' | 'fit' | 'choice' | string
  options: string[]
  fields: string[]
}

/** How ready a task is to be acted on and planned (docs/TASK_FORM_FIELD_CONTRACT_2026-09-04.md §3).
 *  Shown as a ring and "N details missing", never as a number. `score` is null once a task is over. */
export interface TaskScore {
  score: number | null
  scored: boolean
  missing: TaskScoreGap[]
  present: string[]
}

/** `active` is what the engines file a task under (prod 2026-09-12: every open
 *  task); to the pages it is the same "not started" as the form's `pending`. */
export type TaskStatus = 'pending' | 'active' | 'in_progress' | 'completed' | 'cancelled' | 'on_hold' | 'lapsed' | 'parked'
export type TargetWindow = 'today' | 'this_week' | 'this_month' | 'someday'

/** Direct parent as the list needs it (task review §P1-7). title is null
 *  when the parent id dangles. */
export interface TaskParent {
  type: 'goal' | 'skill' | 'aspiration' | 'dream'
  id: number
  title: string | null
  life_area?: string | null
}

/** A step on the task's own checklist (owner 2026-09-05). */
export interface TaskStep {
  id: string
  text: string
  detail?: string | null
  minutes?: number | null
  done: boolean
  done_at?: string | null
  created_at?: string
  source: 'ai' | 'user'
}

export interface TaskHistoryEvent {
  at: string
  kind: 'created' | 'started' | 'completed' | 'progress' | 'note' | 'step' | 'reminder' | 'deferred' | string
  title: string
  detail?: string | null
}

export interface TaskNote {
  id: number
  task_id: number
  text: string
  created_at: string
}

export interface Task {
  id: number
  title: string
  description?: string
  status: TaskStatus
  progress: number
  priority: 'low' | 'medium' | 'high' | 'urgent'
  parent_dream_id?: number
  parent_aspiration_id?: number
  parent_goal_id?: number
  parent_skill_id?: number
  due_date?: string
  /** Deadline "hold me to it" (2026-08-30). due_time is local "HH:MM";
   *  deadline_timezone is snapshotted server-side at capture so changing your
   *  profile timezone later never moves a deadline you already committed to. */
  due_time?: string | null
  deadline_timezone?: string | null
  hold_lead_minutes?: number | null
  hold_interval_minutes?: number | null
  estimated_duration?: number
  category?: string
  task_type?: string
  complexity_level?: string
  is_orphan?: boolean
  location?: string | null
  place_id?: number | null
  // F7 Commitment Tiers: user-declared standing (null/absent = default)
  user_tier?: 'committed' | 'backlog' | null
  // Recurring fields
  is_recurring?: boolean
  recurrence_pattern?: string
  recurrence_rule?: RecurrenceRule | string
  recurrence_end_date?: string
  next_occurrence?: string
  parent_recurring_id?: number
  ai_suggested_recurrence?: string | Record<string, any>  // AI scheduling preferences JSON
  startability?: 'direct_actionable' | 'needs_help'
  has_ai_breakdown?: boolean
  // CON-1: deadline trio must round-trip through edit (was silently dropped)
  deadline_type?: 'soft' | 'hard'
  deadline_consequence?: string
  deadline_risk_level?: 'low' | 'medium' | 'high' | 'critical'
  created_at?: string
  updated_at?: string
  // Task form/field contract (2026-09-04)
  target_window?: TargetWindow | null
  life_area?: string | null
  /** All areas; the first is the primary that `life_area` mirrors. */
  life_areas?: string[] | null
  context?: string | null
  energy_level_required?: 'low' | 'medium' | 'high' | null
  acceptance_criteria?: string | null
  minimum_starter_step?: string | null
  /** Why it matters, in their words — owed by a standalone task that is more than an errand. */
  motivation?: string | null
  motivation_level?: 'low' | 'medium' | 'high' | null
  scope?: 'micro' | 'small' | 'medium' | 'large' | null
  created_via?: string | null
  start_date?: string | null
  started_at?: string | null
  finished_at?: string | null
  actual_duration?: number | null
  completion_date?: string | null
  /** Hard complete-by lifecycle (2026-09-05). */
  lapsed_at?: string | null
  deadline_outcome?: 'met' | 'late' | 'lapsed' | null
  /** component -> ISO date the person skipped that question; asked again after 7 days. */
  score_skipped?: Record<string, string> | null
  task_score?: TaskScore
  parent?: TaskParent | null
  /** Action items under this task (detail only). */
  step_count?: number
  steps?: TaskStep[]
  child_action_items?: Array<{ id: string | number; title: string; status: string; created_by?: string }>
  /** Progress ring only means something for tasks that accrue. */
  incremental?: boolean
  repeatable?: boolean
  sessions_target?: number | null
  sessions_done?: number | null
}

export interface BreakdownActionItem {
  id: number
  title: string
  description?: string
  status: string
  progress: number
  priority: string
  category: string
  created_at?: string
}

export interface CreateTaskRequest {
  title: string
  description?: string
  /** The chat engine's item this page copy was accepted from (2026-09-12),
   *  so a refinement said in a later turn can reach the copy. Absent for
   *  everything the person typed on the pages themselves. */
  lifecoach_hypothesis_id?: number | null
  /** The engine turns the item was said in — identity for binding a chat
   *  reminder asked in one of them (2026-09-12). */
  lifecoach_turn_ids?: number[] | null
  priority?: 'low' | 'medium' | 'high' | 'urgent'
  parent_dream_id?: number | null
  parent_aspiration_id?: number | null
  parent_goal_id?: number | null
  parent_skill_id?: number | null
  due_date?: string
  /** Deadline "hold me to it" (2026-08-30). due_time is local "HH:MM";
   *  deadline_timezone is snapshotted server-side at capture so changing your
   *  profile timezone later never moves a deadline you already committed to. */
  due_time?: string | null
  deadline_timezone?: string | null
  hold_lead_minutes?: number | null
  hold_interval_minutes?: number | null
  estimated_duration?: number
  category?: string
  task_type?: string
  status?: TaskStatus
  progress?: number
  complexity_level?: string
  location?: string | null
  place_id?: number | null
  // Recurring fields
  is_recurring?: boolean
  recurrence_pattern?: string
  recurrence_rule?: RecurrenceRule | string
  recurrence_end_date?: string
  // CON-1: deadline trio
  deadline_type?: 'soft' | 'hard'
  deadline_consequence?: string
  deadline_risk_level?: 'low' | 'medium' | 'high' | 'critical'
  // Task form/field contract (2026-09-04)
  target_window?: TargetWindow | null
  life_area?: string | null
  context?: string | null
  energy_level_required?: 'low' | 'medium' | 'high' | null
  acceptance_criteria?: string | null
  minimum_starter_step?: string | null
  motivation?: string | null
  motivation_level?: 'low' | 'medium' | 'high' | null
  created_via?: string
  user_tier?: 'committed' | 'backlog' | null
  actual_duration?: number | null
  score_skipped?: Record<string, string> | null
}

export interface UpdateTaskRequest {
  lifecoach_turn_ids?: number[] | null
  title?: string
  description?: string
  status?: TaskStatus
  progress?: number
  priority?: 'low' | 'medium' | 'high' | 'urgent'
  // F7 Commitment Tiers: user-declared standing; null clears the declaration
  user_tier?: 'committed' | 'backlog' | null
  parent_dream_id?: number | null
  parent_aspiration_id?: number | null
  parent_goal_id?: number | null
  parent_skill_id?: number | null
  due_date?: string | null
  /** Deadline "hold me to it" (2026-08-30). due_time is local "HH:MM";
   *  deadline_timezone is snapshotted server-side at capture so changing your
   *  profile timezone later never moves a deadline you already committed to. */
  due_time?: string | null
  deadline_timezone?: string | null
  hold_lead_minutes?: number | null
  hold_interval_minutes?: number | null
  estimated_duration?: number
  category?: string
  task_type?: string
  complexity_level?: string
  location?: string | null
  place_id?: number | null
  // Recurring fields
  is_recurring?: boolean
  recurrence_pattern?: string
  recurrence_rule?: RecurrenceRule | string
  recurrence_end_date?: string
  // CON-1: deadline trio
  deadline_type?: 'soft' | 'hard'
  deadline_consequence?: string | null
  deadline_risk_level?: 'low' | 'medium' | 'high' | 'critical'
  // Task form/field contract (2026-09-04)
  target_window?: TargetWindow | null
  life_area?: string | null
  context?: string | null
  energy_level_required?: 'low' | 'medium' | 'high' | null
  acceptance_criteria?: string | null
  minimum_starter_step?: string | null
  motivation?: string | null
  motivation_level?: 'low' | 'medium' | 'high' | null
  created_via?: string
  actual_duration?: number | null
  score_skipped?: Record<string, string> | null
  steps?: Array<Partial<TaskStep> & { text: string }>
  sessions_done?: number | null
  sessions_target?: number | null
  life_areas?: string[] | null
}

export class TaskService {
  private baseUrl = '/api/hierarchy/tasks'

  async getTasks(): Promise<Task[]> {
    const response = await fetch(this.baseUrl, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch tasks' }))
      throw new Error(error.error || 'Failed to fetch tasks')
    }
    const data = await response.json()
    console.log('Tasks API response:', data)
    // Handle array response - backend returns array directly from controller
    const tasks = Array.isArray(data) ? data : data.data || []
    console.log('Parsed tasks:', tasks)
    return tasks
  }

  async getTask(id: number): Promise<Task> {
    // Use the generic item endpoint: /api/hierarchy/item/<item_type>/<item_id>
    const response = await fetch(`/api/hierarchy/item/task/${id}`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: `Failed to fetch task with id ${id}` }))
      throw new Error(error.error || `Failed to fetch task with id ${id}`)
    }
    const result = await response.json()
    // The backend returns { data: { item: {...}, parent_chain: [...], children: {...} } }
    const item = result.data?.item || result.item || result
    const children = result.data?.children || result.children || {}
    // If we need full details, fetch from list and find the item
    if (!item.title) {
      const allTasks = await this.getTasks()
      const fullTask = allTasks.find(t => t.id === id)
      if (fullTask) return fullTask
    }
    const actionItems = children.action_items || []
    const hasAiBreakdown = actionItems.some((ai: any) => ai.created_by === 'ai_breakdown')
    return {
      // Look-and-feel pass 2026-09-05: this reshape used to DROP everything it
      // did not name — task_score, parent, target_window, life_area, why, the
      // Start/Finish stamps — so the detail page never saw the contract fields
      // the server had been sending since P0. Spread first; normalise after.
      ...item,
      step_count: actionItems.length,
      child_action_items: actionItems.map((ai: { id: string | number; title: string; status?: string; created_by?: string }) => ({ id: ai.id, title: ai.title, status: ai.status || 'pending', created_by: ai.created_by })),
      id: item.id,
      title: item.title || '',
      description: item.description,
      status: item.status || 'pending',
      progress: item.progress || 0,
      priority: item.priority || 'medium',
      parent_dream_id: item.parent_dream_id,
      parent_aspiration_id: item.parent_aspiration_id,
      parent_goal_id: item.parent_goal_id,
      parent_skill_id: item.parent_skill_id,
      due_date: item.due_date,
      // Deadline "hold me to it". This reshape DROPS anything not named —
      // the file's own comments record place_id and user_tier being lost here
      // exactly this way, each time making every save wipe the field.
      due_time: item.due_time,
      deadline_timezone: item.deadline_timezone,
      hold_lead_minutes: item.hold_lead_minutes,
      hold_interval_minutes: item.hold_interval_minutes,
      estimated_duration: item.estimated_duration,
      category: item.category,
      task_type: item.task_type,
      complexity_level: item.complexity_level,
      is_orphan: item.is_orphan,
      is_recurring: item.is_recurring,
      recurrence_pattern: item.recurrence_pattern,
      recurrence_rule: item.recurrence_rule,
      recurrence_end_date: item.recurrence_end_date,
      next_occurrence: item.next_occurrence,
      ai_suggested_recurrence: item.ai_suggested_recurrence,
      startability: item.startability,
      has_ai_breakdown: hasAiBreakdown,
      // CON-1: preserve the deadline trio so the edit form can round-trip it
      deadline_type: item.deadline_type,
      deadline_consequence: item.deadline_consequence,
      deadline_risk_level: item.deadline_risk_level,
      // Location-based reminders: the backend serialises place_id in
      // get_item_details, but the explicit field-by-field reshape above used
      // to drop it, so the edit form read back `undefined` and rendered the
      // "Remind me at" field as empty — making every save look like a no-op.
      place_id: item.place_id ?? null,
      location: item.location ?? null,
      // F7 Commitment Tiers: same reshape-drop hazard as place_id above —
      // without this the edit form reads back undefined and every save
      // clears the user's declared tier.
      user_tier: item.user_tier ?? null,
      created_at: item.created_at,
      updated_at: item.updated_at || item.created_at
    }
  }

  async createTask(task: CreateTaskRequest): Promise<Task> {
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(task)
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to create task' }))
      throw new Error(error.error || 'Failed to create task')
    }
    const data = await response.json()
    return data.data || data
  }

  async updateTask(id: number, updates: UpdateTaskRequest): Promise<Task> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to update task' }))
      throw new Error(error.error || 'Failed to update task')
    }
    const data = await response.json()
    return data.data || data
  }

  async getTaskHistory(id: number): Promise<TaskHistoryEvent[]> {
    const response = await fetch(`${this.baseUrl}/${id}/history`, { credentials: 'include' })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to load history' }))
      throw new Error(error.error || 'Failed to load history')
    }
    const body = await response.json()
    return (body.data || []) as TaskHistoryEvent[]
  }

  async addTaskNote(id: number, text: string): Promise<TaskNote> {
    const response = await fetch(`${this.baseUrl}/${id}/notes`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text }),
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to save note' }))
      throw new Error(error.error || 'Failed to save note')
    }
    const body = await response.json()
    return body.data as TaskNote
  }

  async deleteTask(id: number): Promise<{ cleanup?: Record<string, number> }> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to delete task' }))
      throw new Error(error.error || 'Failed to delete task')
    }
    // The server cascades (schedule blocks, Today pins, reminders, steps) and
    // reports the counts so the page can say what went with it.
    const body = await response.json().catch(() => ({} as { cleanup?: Record<string, number> }))
    // Resync geofences: a deleted task may have been the sole owner of a fence.
    void import('./geofenceSyncService').then(m => m.geofenceSyncService.reconcile())
    return { cleanup: body.cleanup }
  }

  async updateTaskProgress(id: number, progress: number): Promise<Task> {
    return this.updateTask(id, { progress })
  }

  async markTaskComplete(id: number): Promise<Task & { journey_step_unlocked?: { id: number; title: string; description?: string; estimated_duration?: number } }> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'completed', progress: 100 })
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to complete task' }))
      throw new Error(error.error || 'Failed to complete task')
    }
    const raw = await response.json()
    const task = raw.data || raw
    if (raw.journey_step_unlocked || raw.data?.journey_step_unlocked) {
      task.journey_step_unlocked = raw.journey_step_unlocked || raw.data.journey_step_unlocked
    }
    // Completed tasks should no longer nudge the user when they walk in.
    void import('./geofenceSyncService').then(m => m.geofenceSyncService.reconcile())
    return task
  }

  async breakdownTask(taskId: number): Promise<{ action_items: BreakdownActionItem[], steps?: TaskStep[], revised_total_duration_minutes?: number, original_estimated_duration?: number }> {
    const response = await fetch(`${this.baseUrl}/${taskId}/breakdown`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to generate breakdown' }))
      throw new Error(error.error || 'Failed to generate breakdown')
    }
    return response.json()
  }

  async getFirstStep(taskId: number): Promise<{ action_item: BreakdownActionItem, revised_total_duration_minutes?: number, original_estimated_duration?: number }> {
    const response = await fetch(`${this.baseUrl}/${taskId}/first-step`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to generate first step' }))
      throw new Error(error.error || 'Failed to generate first step')
    }
    return response.json()
  }
}
